import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import AddressPanel from 'components/AddressPanel/AddressPanel';
import DataSetsPanel from 'components/DataSetsPanel/DataSetsPanel';
import DataSetsRecordButtons from 'components/DataSetsRecordButtons/DataSetsRecordButtons';
import DatePicker from 'components/DatePicker/DatePicker';
import DetailSection from 'components/DetailSection/DetailSection';
import Loader from 'components/Loader/Loader';
import Modal from 'components/Modal/Modal';
import NumberField from 'components/NumberField/NumberField';
import Panel from 'components/Panel/Panel';
import Select from 'components/Select/Select';
import Separator from 'components/Separator/Separator';
import {
  END_DATE, NET_BOOK, NET_BOOK_CURRENCY, PROFIT_CENTER_IDS, START_DATE,
} from 'constants/formFields';
import { DATE_FORMAT } from 'constants/general';

import GeneralDataSetsPanel from './components/GeneralDataSetsPanel';

import type { ICreateOwnOperationsView } from './interfaces/ICreateOwnOperationsView';

import 'react-datepicker/dist/react-datepicker.css';
import styles from './sass/CreateOwnOperationsView.module.scss';

function CreateOwnOperationsView({
  control, onOwnOperationsSubmit, isConfirmationModalOpen, onHandleCloseConfirmation,
  onDiscardChangesConfirm, enumData, naceCodesData, naceDivisions, onNaceSelectionChange, profitCenters, isPageLoading,
  onFindCountries, onFindStates, onFindCities, isStatesDisabled, isCitiesDisabled, onChangeCountry, onChangeState,
  dateOpened, onDateOpenedChange, startDate, onStartDateChange, defaultCountries,
  setValue, vcOptionsList, handleGetVcOptions, listPosition,
}: ICreateOwnOperationsView) {
  const { t } = useTranslation();
  const { recordId = '' } = useParams();
  const placeholder = recordId ? t('common.notProvided') : '';

  return (
    <div className={styles.root}>
      <Panel className={styles.panel}>
        <div className={styles.wrapper}>
          <Loader isPageLoading={isPageLoading} isFullLoader={false} />
          <GeneralDataSetsPanel
            control={control}
            enumData={enumData}
            recordId={recordId}
            placeholder={placeholder}
            dateOpened={dateOpened}
            onDateOpenedChange={onDateOpenedChange}
            setValue={setValue}
            vcOptionsList={vcOptionsList}
            handleGetVcOptions={handleGetVcOptions}
            listPosition={listPosition}
          />
          <DetailSection
            control={control}
            naceCodesData={naceCodesData}
            naceDivisions={naceDivisions}
            onNaceSelectionChange={onNaceSelectionChange}
            placeholder={placeholder}
            recordId={recordId}
          />
          <DataSetsPanel title={t('ownOperations.selections.financials')} className={styles.fullWidth}>
            <div className={styles.wrapper}>
              <Controller
                control={control}
                name={PROFIT_CENTER_IDS}
                render={({
                  field: {
                    name,
                    value,
                    onChange,
                  },
                  fieldState: { error },
                }) => (
                  <Select
                    name={name}
                    label={t('ownOperations.fields.profitCentreID')}
                    placeholder={recordId ? placeholder : t('ownOperations.fields.profitCentre')}
                    options={profitCenters}
                    value={value || undefined}
                    error={error?.message}
                    onChange={onChange}
                    isSearchable
                    required
                    isMulti
                  />
                )}
              />
            </div>
            <div className={styles.wrapper}>
              <Controller
                name={START_DATE}
                control={control}
                render={({
                  field: {
                    name,
                    onChange,
                    value,
                  },
                  fieldState: { error },
                }) => (
                  <DatePicker
                    error={error?.message}
                    label={t('ownOperations.fields.startDate')}
                    onChange={(date) => {
                      onStartDateChange(date);
                      onChange(date);
                    }}
                    value={value}
                    name={name}
                    maxDate={new Date()}
                    placeholder={placeholder || DATE_FORMAT}
                    required
                  />
                )}
              />
              <Controller
                name={END_DATE}
                control={control}
                render={({
                  field: {
                    name,
                    value,
                    onChange,
                  },
                  fieldState: { error },
                }) => (
                  <DatePicker
                    label={t('ownOperations.fields.endDate')}
                    placeholder={placeholder || DATE_FORMAT}
                    name={name}
                    onChange={onChange}
                    minDate={startDate}
                    value={value}
                    error={error?.message}
                    required
                  />
                )}
              />
            </div>
            <div className={styles.wrapper}>
              <Controller
                control={control}
                name={NET_BOOK}
                render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                  <div>
                    <div className={styles.fieldWithCurrency}>
                      <NumberField
                        label={t('ownOperations.fields.netBook')}
                        name={name}
                        value={value}
                        error={error?.message}
                        onChange={(values) => onChange(values.value)}
                        placeholder={t('ownOperations.fields.netBook')}
                        className={styles.valueInput}
                        allowNegative={false}
                        hideTextError
                        required
                      />
                      <Controller
                        control={control}
                        name={NET_BOOK_CURRENCY}
                        render={({
                          field:
                        { value: valueCurrency },
                        }) => (
                          <span className={`${styles.currencyInput} ${error?.message ? styles.errorBorder : ''}`}>
                            {`M, ${valueCurrency}`}
                          </span>
                        )}
                      />
                    </div>
                    {error?.message ? <span className={styles.errorMessage}>{error.message}</span> : null}
                  </div>
                )}
              />
            </div>
          </DataSetsPanel>
          <AddressPanel
            control={control}
            placeholder={placeholder}
            onFindCountries={onFindCountries}
            onFindStates={onFindStates}
            onFindCities={onFindCities}
            isStatesDisabled={isStatesDisabled}
            isCitiesDisabled={isCitiesDisabled}
            onChangeCountry={onChangeCountry}
            onChangeState={onChangeState}
            defaultCountries={defaultCountries}
            requiredState
          />
        </div>
        <Separator />
        <Modal
          open={isConfirmationModalOpen}
          title={t('createNewUser.cancelModalTitle')}
          description={t('clients.cancelModalText')}
          confirmTitle={t('common.discardChanges')}
          closeTitle={t('common.keepEditing')}
          confirmVariant="error"
          onClose={() => onHandleCloseConfirmation(false)}
          onReject={() => onHandleCloseConfirmation(false)}
          onConfirm={onDiscardChangesConfirm}
          isLoading={isPageLoading}
        />
        <DataSetsRecordButtons
          onClose={onHandleCloseConfirmation}
          onSubmit={onOwnOperationsSubmit}
          isLoading={isPageLoading}
        />
      </Panel>
    </div>
  );
}

export default CreateOwnOperationsView;
